import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, List, ListItem, ListItemText, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import { ETYKIETA_STANU, tresciApi, type Skorka, type WpisHistorii } from '@/lib/api/tresci';
import { DOPISEK_WYNIK_NIEZNANY, komunikatBledu, wWarszawie, wynikNieznany } from '@/lib/tresci/tresciUi';

export interface SkorkiListaProps {
  odswiez: number;
  mozePublikowac: boolean;
  /** Serwer CC potwierdził drogę zapisu (`/stan`). Bez niej akcje są widoczne, ale wyłączone. */
  zapisDostepny: boolean;
  /** Okno z harmonogramu na ekranie (UTC) — używane przez „Zaplanuj", „Aktywuj teraz" i „Rollback". */
  okno: { startsAt: string | null; endsAt: string | null; bezKonca: boolean; ok: boolean };
  /** Po udanej akcji — biblioteka nad listą czyta się ponownie (OWNER 11.10). */
  onZmiana?: () => void;
}

/**
 * Rewizje skórek ze stanem efektywnym i akcjami. V1: najwyżej JEDNA aktywna skórka — system główny
 * odmawia nachodzących okien (409) i panel pokazuje to operatorowi, zamiast wybierać po cichu.
 */
export function SkorkiLista({ odswiez, mozePublikowac, zapisDostepny, okno, onZmiana }: SkorkiListaProps) {
  // Skórka bez końca nie wygaśnie sama (kontrakt R5-10: `doKiedy: null` = bez ograniczenia), więc
  // „Aktywuj teraz" i „Rollback" wymagają końca okna albo ŚWIADOMEGO „bez daty końcowej" —
  // ta sama reguła co przy pasku. Puste pole nie może znaczyć „na zawsze".
  const koniecUstalony = okno.endsAt !== null || okno.bezKonca;
  const { api } = useServices();
  const [skorki, setSkorki] = useState<Skorka[] | null>(null);
  // Dwa różne błędy: listy (odczyt) i akcji (zapis). Udany odczyt po akcji nie może skasować błędu akcji.
  const [bladListy, setBladListy] = useState<string | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [licznik, setLicznik] = useState(0);
  const [odczytano, setOdczytano] = useState<Date | null>(null);
  const [historia, setHistoria] = useState<{ themeId: string; wpisy: WpisHistorii[] } | null>(null);

  useEffect(() => {
    tresciApi.skorki(api)
      .then((s) => { setSkorki(s); setOdczytano(new Date()); setBladListy(null); })
      .catch((e: unknown) => setBladListy(komunikatBledu(e)));
  }, [api, odswiez, licznik]);

  /*
   * Po udanej akcji (Zaplanuj, Aktywuj, Wyłącz, Rollback) lista czyta się ponownie z systemu głównego — bez F5
   * (OWNER 10.10). Po ODRZUCONEJ akcji (4xx) lista zostaje bez zmian, a powód jest w alercie (B8 z recenzji
   * #833: odmowa nie może wyglądać na zmianę). Wynik NIEZNANY (sieć, przekroczony czas, 5xx) to nie odmowa:
   * lista czyta się ponownie, a alert mówi, że operacja mogła się wykonać.
   */
  const akcja = useCallback(async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      setBlad(null);
      setLicznik((x) => x + 1);
      onZmiana?.();
    } catch (e) {
      const nieznany = wynikNieznany(e);
      setBlad(komunikatBledu(e) + (nieznany ? DOPISEK_WYNIK_NIEZNANY : ''));
      if (nieznany) setLicznik((x) => x + 1);
    }
  }, [onZmiana]);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="skorki-odczytano">
          {odczytano ? `Odczytano ${odczytano.toLocaleTimeString('pl-PL', { timeZone: 'Europe/Warsaw' })}` : 'Wczytywanie…'}
        </Typography>
        <Button size="small" onClick={() => setLicznik((x) => x + 1)}>Odśwież</Button>
      </Stack>
      {bladListy ? <Alert severity="error" role="alert" sx={{ mb: 1 }}>Nie udało się odczytać listy skórek: {bladListy}</Alert> : null}
      {blad ? <Alert severity="error" role="alert" sx={{ mb: 1 }}>{blad}</Alert> : null}
      {mozePublikowac && skorki && skorki.length > 0 && zapisDostepny && !koniecUstalony ? (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
          „Aktywuj teraz" i „Rollback": ustaw w „Kiedy" koniec okna albo świadomie zaznacz „bez daty końcowej".
        </Typography>
      ) : null}
      {skorki && skorki.length === 0 ? <Typography variant="body2">Brak skórek — aplikacja ma zwykły wygląd.</Typography> : null}
      {skorki && skorki.length > 0 ? (
        <Table size="small" aria-label="Rewizje skórek">
          <TableHead>
            <TableRow>
              <TableCell>Stan</TableCell><TableCell>Nazwa</TableCell><TableCell>Rewizja</TableCell>
              <TableCell>Od (Warszawa)</TableCell><TableCell>Do</TableCell><TableCell>Utworzył</TableCell>
              <TableCell>Ostatnia zmiana</TableCell><TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {skorki.map((s) => {
              const st = s.effectiveStatus;
              return (
                <TableRow key={s.id} data-testid={`skorka-${s.id}`}>
                  <TableCell>
                    <Tooltip title={s.validationErrors.join(' · ') || ''}>
                      <Chip size="small" color={st === 'ERROR' ? 'error' : st === 'ACTIVE' ? 'success' : 'default'}
                        label={ETYKIETA_STANU[st] ?? st} />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.revision}</TableCell>
                  <TableCell>{wWarszawie(s.startsAt)}</TableCell>
                  <TableCell>{s.endsAt ? wWarszawie(s.endsAt) : 'bez końca'}</TableCell>
                  <TableCell>{s.createdBy ?? '—'}</TableCell>
                  <TableCell>
                    {wWarszawie(s.updatedAt)}
                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>{s.updatedBy ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {mozePublikowac && (st === 'DRAFT' || st === 'SCHEDULED') ? (
                        <>
                          <Button size="small" disabled={!zapisDostepny || !okno.ok || !okno.startsAt}
                            onClick={() => akcja(() => tresciApi.zaplanujSkorke(api, s.id, { startsAt: okno.startsAt, endsAt: okno.endsAt }))}>Zaplanuj</Button>
                          <Button size="small" variant="contained" disabled={!zapisDostepny || !koniecUstalony}
                            onClick={() => akcja(() => tresciApi.aktywujSkorke(api, s.id, okno.endsAt))}>Aktywuj teraz</Button>
                        </>
                      ) : null}
                      {mozePublikowac && (st === 'ACTIVE' || st === 'SCHEDULED' || st === 'DRAFT') ? (
                        <Button size="small" color="warning" disabled={!zapisDostepny} onClick={() => akcja(() => tresciApi.wylaczSkorke(api, s.id))}>Wyłącz</Button>
                      ) : null}
                      {mozePublikowac && (st === 'DISABLED' || st === 'ENDED') ? (
                        <Button size="small" disabled={!zapisDostepny || !koniecUstalony} onClick={() => akcja(() => tresciApi.rollbackSkorki(api, s.id, okno.endsAt))}>Rollback</Button>
                      ) : null}
                      <Button size="small" onClick={() => tresciApi.historiaSkorki(api, s.themeId)
                        .then((w) => setHistoria({ themeId: s.themeId, wpisy: w })).catch((e: unknown) => setBlad(komunikatBledu(e)))}>Historia</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}

      <Dialog open={historia !== null} onClose={() => setHistoria(null)} fullWidth>
        <DialogTitle>Historia skórki {historia?.themeId}</DialogTitle>
        <DialogContent>
          <List dense>
            {historia?.wpisy.map((h) => (
              <ListItem key={h.id}>
                <ListItemText primary={`${h.action} · ${h.actor ?? '—'} · rewizja #${h.revisionId ?? '?'}`}
                  secondary={`${wWarszawie(h.at)}${h.note ? ' · ' + h.note : ''}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
